import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckCircle2, ArrowUpRight, Package, Target } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Reveal, Overline, SectionHeader } from '../components/Reveal';
import { CTASection } from '../components/CTASection';
import { ReadinessCalculator } from '../components/ReadinessCalculator';
import { useSEO } from '../lib/seo';
import { SERVICES, SERVICE_CATEGORIES, APPROACH } from '../data/content';

const EASE = [0.16, 1, 0.3, 1];

const ServicesHero = () => (
    <section className="relative overflow-hidden pt-44 pb-20 sm:pb-24" data-testid="services-hero">
        <div className="absolute inset-0 grid-lines opacity-60" />
        <div className="pointer-events-none absolute -top-24 right-0 h-96 w-96 rounded-full bg-deepteal/10 blur-[140px]" />
        <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: EASE }}>
                <Overline>Services</Overline>
            </motion.div>
            <h1 className="mt-6 max-w-4xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-slate-50">
                {['Every discipline of', 'organizational resilience.'].map((line, i) => (
                    <span key={line} className="block overflow-hidden pb-1">
                        <motion.span
                            className="block"
                            initial={{ y: '110%' }}
                            animate={{ y: '0%' }}
                            transition={{ duration: 0.9, delay: 0.2 + i * 0.14, ease: EASE }}
                        >
                            {i === 1 ? <>organizational <span className="text-deepteal">resilience.</span></> : line}
                        </motion.span>
                    </span>
                ))}
            </h1>
            <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.65, ease: EASE }}
                className="mt-7 max-w-2xl text-base sm:text-lg leading-relaxed text-slate-300"
            >
                Ten core services covering the full resilience lifecycle — from business impact analysis
                to rehearsed recovery. Each delivered with defined deliverables and measurable outcomes.
            </motion.p>
        </div>
    </section>
);

const ServiceBlock = ({ service, index }) => (
    <motion.article
        layout
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -16 }}
        transition={{ duration: 0.55, ease: EASE }}
        className="border-t border-slate-800 py-14 first:border-t-0"
        data-testid={`service-detail-${service.id}`}
    >
        <div className="grid grid-cols-1 gap-10 lg:grid-cols-12">
            <div className="lg:col-span-4">
                <div className="flex items-center gap-4">
                    <span className="font-mono text-sm text-slate-500">{service.num}</span>
                    <span className="rounded-full border border-deepteal/20 bg-deepteal/5 px-3 py-1 font-mono text-[10px] uppercase tracking-[0.15em] text-deepteal">
                        {service.category}
                    </span>
                </div>
                <h2 className="mt-5 font-display text-2xl sm:text-3xl font-bold tracking-tight text-slate-50 text-balance">
                    {service.title}
                </h2>
                <div className="mt-6 flex h-12 w-12 items-center justify-center rounded-xl border border-deepteal/20 bg-deepteal/10 text-deepteal">
                    <service.icon size={22} />
                </div>
            </div>

            <div className="lg:col-span-8">
                <p className="text-base sm:text-lg leading-relaxed text-slate-300">{service.description}</p>

                <div className="mt-8 rounded-2xl border border-deepteal/15 bg-navy-800/40 p-6">
                    <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-deepteal">Business value</p>
                    <p className="mt-2 text-sm sm:text-base leading-relaxed text-slate-200">{service.value}</p>
                </div>

                <div className="mt-8 grid grid-cols-1 gap-8 md:grid-cols-2">
                    <div>
                        <div className="flex items-center gap-2">
                            <Package size={15} className="text-deepteal/80" />
                            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Typical deliverables</h3>
                        </div>
                        <ul className="mt-4 space-y-3">
                            {service.deliverables.map((d) => (
                                <li key={d} className="flex gap-3 text-sm leading-relaxed text-slate-400">
                                    <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-deepteal" />
                                    {d}
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div>
                        <div className="flex items-center gap-2">
                            <Target size={15} className="text-deepteal/80" />
                            <h3 className="font-mono text-xs uppercase tracking-[0.2em] text-slate-400">Expected outcomes</h3>
                        </div>
                        <ul className="mt-4 space-y-3">
                            {service.outcomes.map((o) => (
                                <li key={o} className="flex gap-3 text-sm leading-relaxed text-slate-400">
                                    <CheckCircle2 size={15} className="mt-0.5 shrink-0 text-deepteal" />
                                    {o}
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    </motion.article>
);

const Methodology = () => (
    <section className="border-t border-slate-800 bg-navy-950 py-20 sm:py-28" data-testid="methodology-section">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
            <SectionHeader
                overline="How we deliver"
                title="A roadmap from first assessment to proven readiness."
            />
            <div className="mt-14 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
                {APPROACH.map((step, i) => (
                    <Reveal key={step.step} delay={i * 0.08}>
                        <div className="h-full rounded-2xl border border-slate-800 bg-navy-800/50 p-7" data-testid={`methodology-step-${i}`}>
                            <span className="font-display text-4xl font-extrabold text-slate-600">{String(i + 1).padStart(2, '0')}</span>
                            <h3 className="mt-4 font-display text-lg font-semibold text-deepteal">{step.step}</h3>
                            <p className="mt-3 text-sm leading-relaxed text-slate-400">{step.text}</p>
                        </div>
                    </Reveal>
                ))}
            </div>
            <Reveal delay={0.2}>
                <div className="mt-12 text-center">
                    <Link
                        to="/contact"
                        data-testid="methodology-cta-link"
                        className="group inline-flex items-center gap-2 text-sm font-semibold text-deepteal"
                    >
                        Start with an assessment
                        <ArrowUpRight size={16} className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                    </Link>
                </div>
            </Reveal>
        </div>
    </section>
);

export default function Services() {
    useSEO(
        'Resilience & Recovery Services | Planmodo',
        'Business continuity planning, business impact analysis, disaster recovery, crisis management, operational resilience and tabletop exercises — delivered by practitioners.'
    );
    const [category, setCategory] = useState('All');
    const filtered = category === 'All' ? SERVICES : SERVICES.filter((s) => s.category === category);

    return (
        <>
            <ServicesHero />
            <section className="pb-20 sm:pb-28" data-testid="services-list-section">
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    <div className="flex flex-wrap gap-3" role="tablist" aria-label="Filter services by domain">
                        {SERVICE_CATEGORIES.map((c) => (
                            <button
                                key={c}
                                role="tab"
                                aria-selected={category === c}
                                data-testid={`service-filter-${c.toLowerCase()}`}
                                onClick={() => setCategory(c)}
                                className={`rounded-full border px-5 py-2.5 text-sm font-medium transition-all duration-200 ${category === c ? 'border-deepteal/50 bg-deepteal/10 text-deepteal' : 'border-slate-700 text-slate-400 hover:border-slate-400 hover:text-slate-200'}`}
                            >
                                {c}
                            </button>
                        ))}
                    </div>
                    <div className="mt-6">
                        <AnimatePresence mode="popLayout">
                            {filtered.map((s, i) => (
                                <ServiceBlock key={s.id} service={s} index={i} />
                            ))}
                        </AnimatePresence>
                    </div>
                </div>
            </section>
            <ReadinessCalculator />
            <Methodology />
            <CTASection
                title="Not sure where your gaps are?"
                text="Start with a recovery readiness assessment. We will map your current capability against your actual risk profile — and show you the shortest path to proven readiness."
            />
        </>
    );
}
